import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, Lock, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Entrada, Rotulo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, post } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { usePapeis, usePermissoes } from './MatrizPermissoes';
import { ArvoreAcesso } from './ArvoreAcesso';
import type { EstadoAcesso } from './arvore-acesso';
import { nomeDoPapel, type Papel } from './tipos';
import { PadroesPapel } from '../../nucleo/notificacoes/PadroesPapel';
import { NiveisPapel } from '../../nucleo/assistentes/NiveisPapel';
import { TextoCortado } from '../../componentes/texto-cortado';

/** Papéis de fábrica (somente leitura) e personalizados (matriz editável), M12.4. */
export function Papeis({ criando, aoFecharCriar }: { criando: boolean; aoFecharCriar: () => void }) {
  const { t } = useTranslation('admin');
  const s = useSessao();
  const [aberto, setAberto] = useState<Papel | null>(null);
  const [duplicando, setDuplicando] = useState<Papel | null>(null);
  const q = usePapeis();
  return (
    <>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <Vazio texto={t('usuarios.papel.vazio')} />
          ) : (
            <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {d.itens.map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => setAberto(p)} className="flex h-full w-full min-w-0 flex-col gap-2 rounded-xl border border-borda bg-sup-1 p-4 text-left hover:bg-sup-2">
                    <span className="flex items-start justify-between gap-2">
                      <TextoCortado className="font-semibold">{nomeDoPapel(t, p)}</TextoCortado>
                      <Selo cor={p.de_fabrica ? 'neutro' : 'roxo'}>
                        {p.de_fabrica && <Lock className="mr-1 size-3" aria-hidden />}
                        {p.de_fabrica ? t('usuarios.papel.deFabrica') : t('usuarios.papel.personalizado')}
                      </Selo>
                    </span>
                    <span className="text-xs text-tinta-3">
                      {t('usuarios.papel.membros', { count: p.membros })} · {t('usuarios.papel.permissoes', { count: p.efetivas.length })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )
        }
      </Estado>
      {aberto && (
        <PapelDrawer
          key={aberto.id}
          papel={aberto}
          podeEditar={s.pode('admin.papeis')}
          aoFechar={() => setAberto(null)}
          aoDuplicar={() => (setDuplicando(aberto), setAberto(null))}
        />
      )}
      {criando && <NovoPapel aoFechar={aoFecharCriar} />}
      {/* Lote 19/10 (item 6): duplicar abre o Novo papel com a matriz da origem; níveis e notificações vão depois de criar. */}
      {duplicando && <NovoPapel key={duplicando.id} origem={duplicando} aoFechar={() => setDuplicando(null)} />}
    </>
  );
}

function PapelDrawer({ papel, podeEditar, aoFechar, aoDuplicar }: { papel: Papel; podeEditar: boolean; aoFechar: () => void; aoDuplicar: () => void }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = usePermissoes();
  const catalogo = new Set((perms.data?.itens ?? []).map((p) => p.codigo));
  const editavel = !papel.de_fabrica && podeEditar;
  const [nome, setNome] = useState(papel.nome);
  // Val1: a árvore sub-sistema > página > ação (as ações são as permissões; as páginas desmarcadas ficam no papel).
  const [estado, setEstado] = useState<EstadoAcesso>({ permissoes: new Set(papel.de_fabrica ? papel.efetivas : papel.permissoes), negadas: new Set(papel.paginas_negadas ?? []) });
  const [excluir, setExcluir] = useState(false);
  const depois = async () => {
    await qc.invalidateQueries({ queryKey: ['/admin/papeis'] });
    await qc.invalidateQueries({ queryKey: ['/admin/membros'] });
    aoFechar();
  };
  const salvar = useMutation({
    mutationFn: () => api('PATCH', `/admin/papeis/${papel.id}`, { nome, permissoes: [...estado.permissoes].filter((c) => catalogo.has(c)), paginas_negadas: [...estado.negadas] }),
    onSuccess: async () => {
      toast.sucesso(tn('comum.salvo'));
      await depois();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: () => del(`/admin/papeis/${papel.id}`),
    onSuccess: async () => {
      setExcluir(false);
      toast.sucesso(t('usuarios.papel.excluido'));
      await depois();
    },
    onError: (e) => {
      setExcluir(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={nomeDoPapel(t, papel)}
      rodape={
        editavel ? (
          <>
            <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluir(true)}>
              {t('usuarios.papel.excluir')}
            </Botao>
            <>
              <Botao variante="fantasma" icone={<Copy className="size-4" />} onClick={aoDuplicar}>
                {t('usuarios.papel.duplicar')}
              </Botao>
              <Botao variante="fantasma" onClick={aoFechar}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" disabled={!nome.trim()} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                {tn('comum.salvar')}
              </Botao>
            </>
          </>
        ) : (
          podeEditar && (
            <Botao icone={<Copy className="size-4" />} onClick={aoDuplicar}>
              {t('usuarios.papel.duplicar')}
            </Botao>
          )
        )
      }
    >
      <div className="flex flex-col gap-4">
        {papel.de_fabrica && <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm text-tinta-2">{t('usuarios.papel.imutavel')}</p>}
        {editavel && (
          <Rotulo id="papel-nome" rotulo={t('usuarios.papel.nomeLabel')}>
            <Entrada id="papel-nome" value={nome} maxLength={60} onChange={(e) => setNome(e.target.value)} />
          </Rotulo>
        )}
        <div>
          <h3 className="kicker mb-2">{t('usuarios.arvore.titulo')}</h3>
          <Estado consulta={perms}>{(d) => <ArvoreAcesso permissoes={d.itens} estado={estado} somenteLeitura={!editavel} aoMudar={setEstado} />}</Estado>
        </div>
        {/* Fase 2 (W3): notificações padrão do papel, também nos papéis de fábrica (salva à parte da matriz). */}
        <PadroesPapel papelId={papel.id} editavel={podeEditar} />
        {/* Fase 2 (W11): nível de risco das ações para os assistentes de quem tem o papel (salva à parte). */}
        <NiveisPapel papelId={papel.id} editavel={podeEditar} />
      </div>
      <Confirmar
        aberto={excluir}
        aoFechar={() => setExcluir(false)}
        aoConfirmar={() => remover.mutate()}
        carregando={remover.isPending}
        perigoso
        titulo={t('usuarios.papel.excluirTitulo', { nome: nomeDoPapel(t, papel) })}
        texto={t('usuarios.papel.excluirTexto')}
      />
    </Drawer>
  );
}

/** Copia para o papel novo o que se grava à parte da matriz: os níveis reclassificados e as notificações padrão. */
async function copiarAjustes(origem: string, destino: string) {
  const niveis = await get<{ itens: { permissao: string; travado: boolean; nivel: number | null }[] }>(`/assistentes/niveis/${origem}`);
  const mudados = niveis.itens.filter((i) => i.nivel !== null && !i.travado).map((i) => ({ permissao: i.permissao, nivel: i.nivel }));
  if (mudados.length) await api('PUT', `/assistentes/niveis/${destino}`, { niveis: mudados });
  const padroes = await get<{ tipos: { chave: string; app: boolean | null; push: boolean | null }[] }>(`/notificacoes/padroes/${origem}`);
  const itens = padroes.tipos.flatMap((p) => (['app', 'push'] as const).filter((c) => p[c] !== null).map((c) => ({ tipo: p.chave, canal: c, ativo: p[c] })));
  if (itens.length) await api('PUT', `/notificacoes/padroes/${destino}`, { itens });
}

function NovoPapel({ aoFechar, origem }: { aoFechar: () => void; origem?: Papel }) {
  const { t } = useTranslation('admin');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = usePermissoes();
  const [nome, setNome] = useState(origem ? t('usuarios.papel.nomeCopia', { nome: nomeDoPapel(t, origem) }).slice(0, 60) : '');
  const [estado, setEstado] = useState<EstadoAcesso>({ permissoes: new Set(origem ? (origem.de_fabrica ? origem.efetivas : origem.permissoes) : []), negadas: new Set(origem?.paginas_negadas ?? []) });
  const catalogo = new Set((perms.data?.itens ?? []).map((p) => p.codigo));
  const criar = useMutation({
    // Na cópia, só vai o que está no catálogo dos módulos instalados (a efetiva do Dono é '*', que não é permissão de matriz).
    mutationFn: () =>
      post<{ id: string }>('/admin/papeis', { nome, permissoes: origem ? [...estado.permissoes].filter((c) => catalogo.has(c)) : [...estado.permissoes], paginas_negadas: [...estado.negadas] }),
    onSuccess: async (r) => {
      if (origem) {
        try {
          await copiarAjustes(origem.id, r.id);
        } catch (e) {
          toast.erro(t('usuarios.papel.copiaParcial', { erro: mensagemDeErro(e) }));
        }
      }
      await qc.invalidateQueries({ queryKey: ['/admin/papeis'] });
      toast.sucesso(t('usuarios.papel.criado'));
      aoFechar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={origem ? t('usuarios.papel.duplicarTitulo', { nome: nomeDoPapel(t, origem) }) : t('usuarios.papel.criarTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!nome.trim()} carregando={criar.isPending} onClick={() => criar.mutate()}>
            {tn('comum.criar')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {origem && <p className="text-sm text-tinta-2">{t('usuarios.papel.duplicarTexto', { nome: nomeDoPapel(t, origem) })}</p>}
        <Rotulo id="novo-papel-nome" rotulo={t('usuarios.papel.nomeLabel')} obrigatorio>
          <Entrada id="novo-papel-nome" value={nome} maxLength={60} onChange={(e) => setNome(e.target.value)} />
        </Rotulo>
        <div>
          <h3 className="kicker mb-2">{t('usuarios.arvore.titulo')}</h3>
          <Estado consulta={perms}>{(d) => <ArvoreAcesso permissoes={d.itens} estado={estado} aoMudar={setEstado} />}</Estado>
        </div>
      </div>
    </Drawer>
  );
}
